// EAGER-safe loader: a memoized dynamic import of the decode chunk. Eager surfaces (grids, maps,
// peek) import THIS statically, but the wkx core / proj4 land only in the split chunk fetched here.
// The function signatures are inlined (not imported from the core) so the eager graph never has a
// static edge to decodeSpatialValue / formatSpatialSummary. The T12 SRID ladder (`./projection/
// sridLadder`, which imports proj4) is dynamic-imported here TOO so proj4 code-splits into this SAME
// lazy chunk (A9/G2) — never a second network chunk, never eagerly reachable.

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type {
  DecodedGeometry,
  SpatialDecodeResult,
  SpatialDeclarationDecodeInput,
  SpatialDimensions,
  SpatialGeoJson,
} from './types';
import type { SridResolution, SridDefCache } from '../projection/sridLadder';

export type SpatialDecoderModule = {
  decodeSpatialValue: (
    engine: DatabaseEngineId,
    value: unknown,
    dataType?: string,
    opts?: { maxBytes?: number },
  ) => SpatialDecodeResult;
  /** Task 18 — decode a user-declared coordinate cell (WKT/GeoJSON/latlng-text/lat-lng pair). */
  decodeDeclaredSpatialValue: (
    declaration: SpatialDeclarationDecodeInput,
    value: unknown,
    opts?: { maxBytes?: number },
  ) => SpatialDecodeResult;
  toWktForExport: (engine: DatabaseEngineId, value: unknown, dataType?: string) => string | null;
  /** Emit WKT directly from a decoded GeoJSON geometry — used by declared cells' Copy WKT (T18). */
  toWkt: (geojson: SpatialGeoJson, dimensions?: SpatialDimensions) => string;
  formatSpatialSummary: (g: DecodedGeometry) => string;
  // T12 SRID ladder — proj4-bearing, so it is reached ONLY through this lazy chunk. The proj4-free
  // consumers (`projectFeaturesForMap`, `computePlanarTransform`, `fetchDbSridDef`) are imported
  // statically at the surface; only these two carry proj4 and must stay lazy.
  resolveSrid: (srid: number | null, defLookup?: (srid: number) => string | null) => SridResolution;
  resolveSridMap: (
    srids: Iterable<number | null>,
    opts?: {
      fetchDef?: (srid: number) => Promise<string | null>;
      cache?: SridDefCache;
      maxDbLookups?: number;
      concurrency?: number;
    },
  ) => Promise<Map<number | null, SridResolution>>;
  // T5 — copy/export parity: transform a single native-spatial cell to WKT for
  // every renderer copy/export pipeline. Same original-value-on-failure
  // contract as decodeSpatialValue/toWktForExport; see exportTransforms.ts.
  // NOTE: (value, engine, dataType) order — matches the standalone
  // `transformSpatialForExport` export the main process imports directly,
  // deliberately NOT the engine-first order of the two siblings above.
  transformSpatialForExport: (value: unknown, engine: DatabaseEngineId, dataType?: string) => unknown;
  /** T18 — copy/export transform for a DECLARED cell: single-column formats emit
   *  WKT; a lat-lng pair returns the RAW value untouched (A8/CD5). */
  transformDeclaredSpatialForExport: (
    value: unknown,
    declaration: SpatialDeclarationDecodeInput,
  ) => unknown;
};

let cached: Promise<SpatialDecoderModule> | null = null;

export function loadSpatialDecoder(): Promise<SpatialDecoderModule> {
  if (!cached) {
    cached = Promise.all([
      import('./decodeSpatialValue'),
      import('./formatSpatialSummary'),
      import('./exportTransforms'),
      // Code-splits proj4 (the SRID ladder) into THIS chunk — the same lazy chunk as the wkx core.
      import('../projection/sridLadder'),
    ]).then(([core, summary, exportTransforms, ladder]) => ({
      decodeSpatialValue: core.decodeSpatialValue,
      decodeDeclaredSpatialValue: core.decodeDeclaredSpatialValue,
      toWktForExport: summary.toWktForExport,
      toWkt: summary.toWkt,
      formatSpatialSummary: summary.formatSpatialSummary,
      transformSpatialForExport: exportTransforms.transformSpatialForExport,
      transformDeclaredSpatialForExport: exportTransforms.transformDeclaredSpatialForExport,
      resolveSrid: ladder.resolveSrid,
      resolveSridMap: ladder.resolveSridMap,
    }));
  }
  return cached;
}
